import Vue from 'vue';
import Item from '@/classes/Item.js';
import { dumbMutation } from '@/store/util.js';
import { itemTypeEnum as ItemTypeEnum } from '@/GeneratedModels/ItemTypeEnum.js';
import { invoiceStatusEnum as InvoiceStatusEnum } from '@/GeneratedModels/InvoiceStatusEnum.js';

export const NotificationMessage = Object.freeze({
  HIGHEST_BID: 'highestBid',
  HIGHEST_INSTANT_BID: 'highestInstantBid',
  HIGHEST_INSTANT_BID_TITLE: 'highestInstantBid',
  EQUAL_BID: 'equalBid',
  HIGHEST_SYMBOLIC_BID: 'highestSymbolicBid',
  EQUAL_SYMBOLIC_BID: 'equalSymbolicBid',
  OUTBID: 'outbid',
  ERROR: 'error',
  SUCCESS: 'success',
  UNAVAILABLE_FOR_BID: 'itemUnavailableForBids',
  UNAVAILABLE_FOR_SYMBOLIC_BID: 'itemUnavailableForSymbolicBid',
  INVALID_QUANTITY: 'itemInvalidQuantity'
});

export const NotificationType = Object.freeze({
  HIGHEST: 'highest',
  EQUAL: 'equal',
  OUTBID: 'outbid',
  ERROR: 'error',
  SUCCESS: 'success',
  UNAVAILABLE: 'itemUnavailableForBids',
  INVALID_QUANTITY: 'itemInvalidQuantity'
});

function handleFeaturedItem(state, item) {
  if (item.featured) {
    Vue.set(state.featuredItems, item.id, item);
  } else {
    Vue.delete(state.featuredItems, item.id);
  }
}

export default {
  setCurrentAuction: dumbMutation('auction'),
  setAllowNuveiCheckoutPayment: dumbMutation('allowNuveiCheckoutPayment'),
  setItems(state, values) {
    // Indexer les items pour simplifier l'accès
    if (!state.items) {
      Vue.set(state, 'items', {});
    }
    for (const item of values) {
      if (item.deletedOn == null) {
        const itemInstance = new Item(item, this.state.app.connectionStaleSequence);
        Vue.set(state.items, item.id, itemInstance);

        if (item.removedOn == null) {
          handleFeaturedItem(state, itemInstance);
        }
      }
    }
  },
  addItem(state, newItem) {
    const itemInstance = new Item(newItem, this.state.app.connectionStaleSequence);

    Vue.set(state.items, newItem.id, itemInstance);
    handleFeaturedItem(state, itemInstance);
  },
  updateItem(state, newItem) {
    const itemInstance = new Item(newItem, this.state.app.connectionStaleSequence);
    Vue.set(state.items, newItem.id, itemInstance);
    handleFeaturedItem(state, itemInstance);
  },
  deleteItem(state, itemId) {
    Vue.delete(state.items, itemId);
    Vue.delete(state.featuredItems, itemId);
  },
  deleteItems(state, itemIds) {
    for (const itemId of itemIds) {
      Vue.delete(state.items, itemId);
      Vue.delete(state.featuredItems, itemId);
    }
  },
  participantsStatusChanged(state, event) {
    if (!event.status) {
      for (const itemId in state.items) {
        const item = state.items[itemId];
        if (item.bids) {
          Vue.set(item, 'bids', item.bids.filter(x => !event.userIds.includes(x.userId)));
        }
      }
      for (const donationId in state.donations) {
        const donation = state.donations[donationId];
        if (event.userIds.includes(donation.userId)) {
          Vue.delete(state.donations, donation.id);
        }
      }
    }
  },
  highestBidEvent(state, event) {
    event.createdOn = new Date(event.createdOn);
    const item = state.items[event.itemId];
    if (item && item.bids) {
      item.modifiedOn = new Date();
      item.bids.unshift(event);
    }

    state.highestBids.unshift(event);
    // this est le store
    if (state.highestBids.length > this.state.app.appSettings.newsFeedBidCount) {
      state.highestBids.splice(this.state.app.appSettings.newsFeedBidCount, state.highestBids.length - this.state.app.appSettings.newsFeedBidCount);
    }

    if (typeof state.itemReassignmentListeners[event.itemId] === 'function') {
      state.itemReassignmentListeners[event.itemId]();
    }
  },
  authHighestBidEvent(state, event) {
    event.createdOn = new Date(event.createdOn);
    const item = state.items[event.itemId];
    if (item && state.correlationIds[item.id] && state.correlationIds[item.id] === event.correlationId) {
      const notificationMessage = event.isInstant ? NotificationMessage.HIGHEST_INSTANT_BID : NotificationMessage.HIGHEST_BID;
      Vue.set(state.notifications, event.itemId, {
        type: NotificationType.HIGHEST,
        message: notificationMessage
      });

      clearTimeout(state.bidsTimeout[item.id]);
      delete state.bidsTimeout[item.id];

      const nextMaxBid = state.userMaxBids[item.id];
      if (nextMaxBid) {
        item.maximalBidInfo = {
          // Si la mise était instant on n'affiche pas les info de mise max
          userHasMaximalBid: !event.isInstant,
          userLastMaximalBidAmount: nextMaxBid
        };
      }
    }
  },
  equalBidEvent(state, event) {
    const item = state.items[event.itemId];
    if (item && state.correlationIds[item.id] && state.correlationIds[item.id] === event.correlationId) {

      if (item.type === ItemTypeEnum.Symbolic) {
        Vue.set(state.notifications, event.itemId, {
          type: NotificationType.EQUAL,
          message: NotificationMessage.EQUAL_SYMBOLIC_BID
        });
      }
      else {
        Vue.set(state.notifications, event.itemId, {
          type: NotificationType.EQUAL,
          message: NotificationMessage.EQUAL_BID
        });
      }

      clearTimeout(state.bidsTimeout[item.id]);
      delete state.bidsTimeout[item.id];
    }
  },
  outbidBidEvent(state, event) {
    const item = state.items[event.itemId];
    if (item && state.correlationIds[item.id] && state.correlationIds[item.id] === event.correlationId) {
      Vue.set(state.notifications, event.itemId, {
        type: NotificationType.OUTBID,
        message: NotificationMessage.OUTBID
      });

      clearTimeout(state.bidsTimeout[item.id]);
      delete state.bidsTimeout[item.id];
    }
  },
  failedBidEvent(state, { itemId, correlationId }) {
    const item = state.items[itemId];

    if (item) {
      Vue.set(state.notifications, itemId, {
        type: NotificationType.ERROR,
        message: NotificationMessage.ERROR
      });

      if (!correlationId || state.correlationIds[itemId] === correlationId) {
        clearTimeout(state.bidsTimeout[item.id]);
        delete state.bidsTimeout[item.id];
      }
    }
  },
  itemPurchasedEvent(state, event) {
    event.createdOn = new Date(event.createdOn);
    const item = state.items[event.itemId];
    if (item && item.purchases) {
      item.modifiedOn = new Date();
      item.purchases.unshift(event);
      item.availableQuantity -= event.quantity;
    }
  },
  authItemPurchaseEvent(state, event) {
    const item = state.items[event.itemId];
    if (item && state.correlationIds[item.id] && state.correlationIds[item.id] === event.identityId) {
      Vue.set(state.notifications, event.itemId, {
        type: NotificationType.HIGHEST,
        message: NotificationMessage.HIGHEST_SYMBOLIC_BID
      });

      clearTimeout(state.bidsTimeout[item.id]);
      delete state.bidsTimeout[item.id];
    }
  },
  itemInvalidTypeEvent(state, event) {
    const item = state.items[event.itemId];
    if (item && state.correlationIds[item.id] && state.correlationIds[item.id] === event.identityId) {
      Vue.set(state.notifications, event.itemId, {
        type: NotificationType.ERROR,
        message: NotificationMessage.ERROR
      });

      clearTimeout(state.bidsTimeout[item.id]);
      delete state.bidsTimeout[item.id];
    }
  },
  itemInvalidQuantityEvent(state, event) {
    const item = state.items[event.itemId];

    if (item) {
      Vue.set(state.notifications, item.id, {
        type: NotificationType.INVALID_QUANTITY,
        message: NotificationMessage.INVALID_QUANTITY
      });

      clearTimeout(state.bidsTimeout[item.id]);
      delete state.bidsTimeout[item.id];
    }
  },
  itemUnavailableForBidsEvent(state, event) {
    const item = state.items[event.itemId];

    if (item) {
      Vue.set(state.notifications, item.id, {
        type: NotificationType.UNAVAILABLE,
        message: NotificationMessage.UNAVAILABLE_FOR_BID
      });

      clearTimeout(state.bidsTimeout[item.id]);
      delete state.bidsTimeout[item.id];
    }
  },
  itemUnavailableForPurchaseEvent(state, event) {
    const item = state.items[event.itemId];

    if (item) {
      Vue.set(state.notifications, item.id, {
        type: NotificationType.UNAVAILABLE,
        message: NotificationMessage.UNAVAILABLE_FOR_SYMBOLIC_BID
      });

      clearTimeout(state.bidsTimeout[item.id]);
      delete state.bidsTimeout[item.id];
    }
  },
  itemAvailabilityChanged(state, event) {
    const item = state.items[event.itemId];
    if (item) {
      item.isAvailable = event.isAvailable;
    }
  },
  itemIsVisibleChanged(state, event) {
    const item = state.items[event.itemId];
    if (item) {
      if (!event.isVisible) {
        Vue.delete(state.items, event.itemId);
        Vue.delete(state.featuredItems, event.itemId);
      }
    }
  },
  successDonationEvent(state, { userId }) {
    Vue.set(state.notifications, userId, {
      type: NotificationType.SUCCESS,
      message: NotificationMessage.SUCCESS
    });
  },
  donationPayNowEvent(state, event) {
    Vue.set(state.notifications, event.userId, {
      type: NotificationType.SUCCESS,
      message: NotificationMessage.SUCCESS,
      invoiceId: event.invoiceId
    });
  },
  failedDonationEvent(state, { userId }) {
    Vue.set(state.notifications, userId, {
      type: NotificationType.ERROR,
      message: NotificationMessage.ERROR
    });
  },
  auctionPaymentEvent(state, event) {
    Vue.set(state.paymentEvents, event, {
      type: NotificationType.SUCCESS,
      message: NotificationMessage.SUCCESS
    });
  },
  auctionPaymentSuccess(state, event) {
    Vue.set(state.paymentEvents, event, {
      type: NotificationType.SUCCESS,
      message: NotificationMessage.SUCCESS
    });
  },
  auctionPaymentFailure(state, event) {
    Vue.set(state.paymentEvents, event, {
      type: NotificationType.SUCCESS,
      message: NotificationMessage.SUCCESS
    });
  },
  bidCanceledEvent(state, bidCanceledEvent) {
    const item = state.items[bidCanceledEvent.itemId];
    if (item && item.bids) {
      for (let i = 0; i < item.bids.length; i++) {
        if (item.bids[i].correlationId === bidCanceledEvent.correlationId) {
          const canceledBid = item.bids.splice(i, 1);
          item.canceledBids.push(...canceledBid);
          break;
        }
      }
    }
    state.highestBids = state.highestBids.filter(x => x.correlationId !== bidCanceledEvent.correlationId);
  },
  donationCanceledEvent(state, donationCanceledEvent) {
    Vue.delete(state.donations, donationCanceledEvent.id.value);
  },
  purchaseCanceledEvent(state, purchaseCanceledEvent) {
    const item = state.items[purchaseCanceledEvent.itemId];
    if (item && item.purchases) {
      for (let i = 0; i < item.purchases.length; i++) {
        if (item.purchases[i].id === purchaseCanceledEvent.correlationId) {
          const canceledPurchase = item.purchases.splice(i, 1);
          item.canceledPurchases.push(...canceledPurchase);
          item.availableQuantity = purchaseCanceledEvent.updatedQuantity;
          break;
        }
      }
    }
  },
  auctionUpdated(state, auction) {
    Vue.set(state, 'auction', auction);
  },
  auctionVisibilityChanged(state, data) {
    Vue.set(state.auction, 'visible', data.auctionVisibility);
  },
  clearNotification(state, itemId) {
    Vue.delete(state.notifications, itemId);
  },
  storeCorrelationId(state, { itemId, correlationId, timeoutId }) {
    Vue.set(state.correlationIds, itemId, correlationId);
    Vue.set(state.bidsTimeout, itemId, timeoutId);
  },
  storeUserMaxBid(state, { itemId, amount }) {
    Vue.set(state.userMaxBids, itemId, amount);
  },
  setError: dumbMutation('error'),
  setLastImpersonatedUsers: dumbMutation('lastImpersonatedUsers'),
  setLastImpersonatedUsersRetrieved(state, success) {
    if (success) {
      state._lastImpersonatedUsersPromiseResolved();
    } else {
      state._lastImpersonatedUsersPromiseRejected();
    }
  },
  setHighestBids: dumbMutation('highestBids'),
  setDonations(state, values) {
    if (!state.donations) {
      Vue.set(state, 'donations', {});
    }
    for (const i in values) {
      const donation = values[i];
      Vue.set(state.donations, donation.id, donation);
    }
  },
  addItemNotificationSuccess(state, itemId) {
    Vue.set(state.notifications, itemId, {
      type: NotificationType.SUCCESS,
      message: NotificationMessage.SUCCESS
    });
  },
  setInvoices(state, invoices) {
    state.invoices = invoices;
  },
  setInvoice(state, event) {
    let index = state.invoices.findIndex(i => i.id === event.invoiceId);
    if (index == -1) {
      index = state.invoices.length;
    }

    Vue.set(state.invoices, index, event.invoice);
  },
  setRefundResponses(state, refundResponses) {
    state.refundResponses = refundResponses;
  },
  setItemPurchaseQuantity(state, { itemId, purchaseId, quantity }) {
    const item = state.items[itemId];
    if (item && item.purchases) {
      const purchase = item.purchases.find(x => x.id === purchaseId);
      if (purchase) {
        purchase.quantity = quantity;
        purchase.total = quantity * purchase.price;
      }
    }
  },
  setShowCategoryFilters: dumbMutation('showCategoryFilters'),
  setShowLocationFilters: dumbMutation('showLocationFilters'),
  setShowPartnerFilters: dumbMutation('showPartnerFilters'),
  setShowTypeFilters: dumbMutation('showTypeFilters'),
  setRaisedAmount(state, { amount, serverTime }) {
    state.raisedAmount = amount;
    this.commit('auction/updateServerTimeOffset', serverTime);
  },
  updateServerTimeOffset(state, serverTime) {
    if (serverTime !== null && serverTime !== undefined) {
      state.serverTimeOffset = new Date() - new Date(serverTime);
      if (process.env.NODE_ENV === 'development') {
        console.debug('serverTimeOffset', state.serverTimeOffset); // eslint-disable-line no-console
      }
    }
  },
  erroneousItemTypeForBidEvent(state, event) {
    const item = state.items[event.itemId];
    if (item && state.correlationIds[item.id] && state.correlationIds[item.id] === event.correlationId) {
      Vue.set(state.notifications, event.itemId, {
        type: NotificationType.ERROR,
        message: NotificationMessage.ERROR
      });

      clearTimeout(state.bidsTimeout[item.id]);
      delete state.bidsTimeout[item.id];
    }
  },
  erroneousAmountForBidEvent(state, event) {
    const item = state.items[event.itemId];
    if (item && state.correlationIds[item.id] && state.correlationIds[item.id] === event.correlationId) {
      Vue.set(state.notifications, event.itemId, {
        type: NotificationType.ERROR,
        message: NotificationMessage.ERROR
      });

      clearTimeout(state.bidsTimeout[item.id]);
      delete state.bidsTimeout[item.id];
    }
  },
  itemsIsFeatureChangedEvent(state, event) {
    for (const item of event) {
      if (item.featured) {
        Vue.set(state.featuredItems, item.id, item);
      } else {
        Vue.delete(state.featuredItems, item.id);
      }
    }
  },
  itemCategoryChanged(state, event) {
    const item = state.items[event.itemId];
    if (item) {
      item.category = event.itemClassification;
    }
  },
  itemLocationChanged(state, event) {
    const item = state.items[event.itemId];
    if (item) {
      item.location = event.itemClassification;
    }
  },
  invoicePaymentStatusChanged(state, event) {
    const invoice = state.invoices.find(x => x.id == event.invoiceId);
    if (invoice) {
      invoice.status = InvoiceStatusEnum.Paid;
      invoice.payment.push(event);
      invoice.transactionFeesPaidByCustomer = event.paidFees;
    }
  },
  invoiceReminderPaymentChanged(state, event) {
    const invoice = state.invoices.find(x => x.id == event);
    if (invoice) {
      invoice.reminderPaymentMailDate = Date.now();
    }
  },
  invoiceStatusError(state, event) {
    const invoice = state.invoices.find(x => x.id == event);
    if (invoice) {
      invoice.status = InvoiceStatusEnum.Error;
    }
  },
  invoiceStatusUpdatedEvent(state, event) {
    const invoice = state.invoices.find(x => x.id === event.invoiceId);
    if (invoice) {
      Vue.set(invoice, 'status', event.invoiceStatus);
    }
  },
  auctionRulesUpdated(state, rules) {
    state.rules = rules;
  },
  itemAvailableQuantityChanged(state, event) {
    const item = state.items[event.itemId];
    if (item) {
      item.isUnlimited = event.isUnlimited;
      item.availableQuantity = event.availableQuantity;
    }
  },
  notifyUserAfterPurchase(state, event) {
    Vue.set(state.notifications, event.itemId, {
      type: NotificationType.HIGHEST,
      message: NotificationMessage.HIGHEST_SYMBOLIC_BID
    });
  },
  setPartners(state, partners) {
    state.partners = partners;
  },
  setFeaturedPartners(state, featuredPartners) {
    state.featuredPartners = featuredPartners;
  },
  addPartner(state, partner) {
    state.partners.push(partner);
    // Gérer aussi featuredPartners si applicable
    if (partner.category && partner.category.showOnHomePage) {
      state.featuredPartners.push(partner);
    }
  },
  updatePartner(state, updatedPartner) {
    const index = state.partners.findIndex(p => p.id === updatedPartner.id);
    if (index !== -1) {
      Vue.set(state.partners, index, updatedPartner);
    }

    // Gérer featuredPartners
    const featuredIndex = state.featuredPartners.findIndex(p => p.id === updatedPartner.id);
    if (updatedPartner.category && updatedPartner.category.showOnHomePage) {
      if (featuredIndex !== -1) {
        Vue.set(state.featuredPartners, featuredIndex, updatedPartner);
      } else {
        state.featuredPartners.push(updatedPartner);
      }
    } else if (featuredIndex !== -1) {
      state.featuredPartners.splice(featuredIndex, 1);
    }
  },
  removePartner(state, partnerId) {
    state.partners = state.partners.filter(p => p.id !== partnerId);
    state.featuredPartners = state.featuredPartners.filter(p => p.id !== partnerId);
  }
};
